import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BookMarked, LogOut, User, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Account() {
  const { user, ready, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "bookmarks" ? "bookmarks" : "profile");
  const [bookmarks, setBookmarks] = useState(null);

  useEffect(() => { if (ready && !user) navigate("/login"); }, [ready, user, navigate]);

  useEffect(() => {
    if (user && tab === "bookmarks") {
      api.get("/bookmarks").then((r) => setBookmarks(r.data.bookmarks)).catch(() => setBookmarks([]));
    }
  }, [user, tab]);

  const removeBookmark = async (id) => {
    try { await api.delete(`/bookmarks/${id}`); setBookmarks((b) => b.filter((x) => x.id !== id)); refreshUser(); toast("Bookmark removed"); }
    catch { toast.error("Could not remove"); }
  };

  if (!user) return <div className="pt-40 text-center text-slate-400">Loading…</div>;

  return (
    <div className="pt-16 lg:pt-[72px] min-h-screen bg-indic-indigo" data-testid="account-page">
      <section className="py-14 border-b border-indic-card-border">
        <div className="mx-auto max-w-4xl px-4 sm:px-8 flex items-center gap-5">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-indic-gold/15 border border-indic-gold/40 text-indic-gold text-2xl font-serif">
            {(user.name || user.email).charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 className="font-serif text-4xl text-amber-50">{user.name || "Reader"}</h1>
            <p className="text-slate-400">{user.email}{user.role === "admin" && <span className="ml-2 text-xs rounded-full border border-indic-gold/40 px-2 py-0.5 text-indic-gold">Admin</span>}</p>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="mx-auto max-w-4xl px-4 sm:px-8">
          <div className="flex gap-2 border-b border-indic-card-border">
            {[["profile", "Profile", User], ["bookmarks", "Bookmarks", BookMarked]].map(([key, label, Icon]) => (
              <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
                className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === key ? "border-indic-gold text-indic-gold" : "border-transparent text-slate-400 hover:text-amber-50"}`}>
                <Icon size={16} /> {label}
              </button>
            ))}
          </div>

          <div className="mt-8">
            {tab === "profile" && (
              <div className="rounded-sm border border-indic-card-border bg-indic-card p-7" data-testid="profile-panel">
                <h2 className="font-serif text-2xl text-amber-50">Your membership</h2>
                <p className="mt-2 text-slate-300/85">INDIC is free to read. Your account keeps your bookmarks and reading in one place across the library.</p>
                <dl className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="rounded-sm border border-indic-card-border p-4"><dt className="text-slate-500">Name</dt><dd className="mt-1 text-amber-50">{user.name || "—"}</dd></div>
                  <div className="rounded-sm border border-indic-card-border p-4"><dt className="text-slate-500">Email</dt><dd className="mt-1 text-amber-50">{user.email}</dd></div>
                  <div className="rounded-sm border border-indic-card-border p-4"><dt className="text-slate-500">Saved passages</dt><dd className="mt-1 text-amber-50">{user.bookmarks?.length || 0}</dd></div>
                  <div className="rounded-sm border border-indic-card-border p-4"><dt className="text-slate-500">Role</dt><dd className="mt-1 text-amber-50 capitalize">{user.role}</dd></div>
                </dl>
                <button onClick={() => { logout(); navigate("/"); toast("Signed out"); }} className="mt-7 inline-flex items-center gap-2 rounded-sm border border-indic-terracotta/50 px-5 py-2.5 text-sm font-semibold text-indic-terracotta hover:bg-indic-terracotta/10 transition-colors" data-testid="account-logout">
                  <LogOut size={15} /> Sign out
                </button>
              </div>
            )}

            {tab === "bookmarks" && (
              <div data-testid="bookmarks-panel">
                {bookmarks === null && <div className="text-slate-400 py-10 text-center">Loading bookmarks…</div>}
                {bookmarks && bookmarks.length === 0 && (
                  <div className="rounded-sm border border-indic-card-border bg-indic-card p-10 text-center">
                    <BookMarked className="mx-auto text-slate-600" size={28} />
                    <p className="mt-4 text-slate-400">No bookmarks yet.</p>
                    <Link to="/library" className="mt-4 inline-block text-indic-gold hover:underline">Browse the library →</Link>
                  </div>
                )}
                {bookmarks && bookmarks.length > 0 && (
                  <div className="space-y-3">
                    {bookmarks.map((b) => (
                      <div key={b.id} className="flex items-center gap-4 rounded-sm border border-indic-card-border bg-indic-card p-4" data-testid={`bookmark-${b.id}`}>
                        <FileText size={18} className="text-indic-gold shrink-0" />
                        <Link to={`/read/${b.id}`} className="flex-1 min-w-0">
                          <div className="text-xs text-slate-500">{b.group_title}</div>
                          <div className="font-serif text-lg text-amber-50 truncate">{b.title}</div>
                        </Link>
                        <button onClick={() => removeBookmark(b.id)} className="text-xs text-indic-terracotta hover:underline shrink-0" data-testid={`remove-bookmark-${b.id}`}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
